import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useModelStatusesStore, STATUS_TYPE_CONFIG } from '../../stores/useModelStatusesStore';
import styles from './ModelStatusEditor.module.css';

interface ModelStatusEditorProps {
  modelId: string;
  modelType: 'chat' | 'image' | 'video';
  modelDisplayName: string;
  onClose: () => void;
}

const EXPIRATION_OPTIONS = [
  { labelKey: 'modelStatus.expiration.1h', hours: 1 },
  { labelKey: 'modelStatus.expiration.2h', hours: 2 },
  { labelKey: 'modelStatus.expiration.4h', hours: 4 },
  { labelKey: 'modelStatus.expiration.12h', hours: 12 },
  { labelKey: 'modelStatus.expiration.24h', hours: 24 },
  { labelKey: 'modelStatus.expiration.3d', hours: 72 },
  { labelKey: 'modelStatus.expiration.infinite', hours: 0 },
];

const STATUS_TYPES = Object.entries(STATUS_TYPE_CONFIG).filter(([key]) => key !== 'custom');

/**
 * ModelStatusEditor — Admin modal for creating/editing model status
 * Appears when admin clicks the [+] button next to a model
 */
export const ModelStatusEditor = ({ modelId, modelType, modelDisplayName, onClose }: ModelStatusEditorProps) => {
  const { t } = useTranslation('common');
  const { getStatus, upsertStatus, deleteStatus } = useModelStatusesStore();
  const existingStatus = getStatus(modelId, modelType);

  const [selectedType, setSelectedType] = useState(existingStatus?.statusType || '');
  const [customText, setCustomText] = useState(existingStatus?.text || '');
  const [expiresInHours, setExpiresInHours] = useState(0);
  const [showInSelector, setShowInSelector] = useState(existingStatus?.showInSelector ?? true);
  const [showInComposer, setShowInComposer] = useState(existingStatus?.showInComposer ?? true);
  const [isSaving, setIsSaving] = useState(false);

  // When selecting a predefined type, auto-fill default text
  useEffect(() => {
    if (selectedType && selectedType !== 'custom') {
      const config = STATUS_TYPE_CONFIG[selectedType];
      if (config && !existingStatus) {
        setCustomText(config.defaultText);
      }
    }
  }, [selectedType, existingStatus]);

  const handleSave = useCallback(async () => {
    if (!selectedType || isSaving) return;
    setIsSaving(true);

    try {
      const typeConfig = STATUS_TYPE_CONFIG[selectedType] || STATUS_TYPE_CONFIG.custom;
      await upsertStatus({
        modelId,
        modelType,
        statusType: selectedType,
        customText: customText || typeConfig.defaultText || selectedType,
        severity: typeConfig.defaultSeverity,
        showInSelector,
        showInComposer,
        isActive: true,
        expiresInHours: expiresInHours > 0 ? expiresInHours : undefined,
      });
      onClose();
    } catch (err) {
      console.error('Failed to save status:', err);
    } finally {
      setIsSaving(false);
    }
  }, [selectedType, customText, expiresInHours, showInSelector, showInComposer, modelId, modelType, upsertStatus, onClose, isSaving]);

  const handleDelete = useCallback(async () => {
    if (!existingStatus) return;
    setIsSaving(true);
    try {
      await deleteStatus(existingStatus.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete status:', err);
    } finally {
      setIsSaving(false);
    }
  }, [existingStatus, deleteStatus, onClose]);

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <div className={styles.title}>{t('modelStatus.title')}</div>
            <div className={styles.subtitle}>{modelDisplayName}</div>
          </div>
          <button className={styles.closeButton} onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Status type grid */}
          <div className={styles.fieldLabel}>{t('modelStatus.statusType')}</div>
          <div className={styles.statusGrid}>
            {STATUS_TYPES.map(([key, config]) => (
              <button
                key={key}
                className={`${styles.statusTypeButton} ${selectedType === key ? styles.selected : ''}`}
                style={{
                  '--status-color': config.textColor,
                  '--status-bg': config.bgColor,
                } as React.CSSProperties}
                onClick={() => setSelectedType(key)}
              >
                <span className={styles.statusTypeDot} style={{ background: config.textColor }} />
                <span className={styles.statusTypeLabel}>{config.label}</span>
              </button>
            ))}
            {/* Custom type */}
            <button
              className={`${styles.statusTypeButton} ${selectedType === 'custom' ? styles.selected : ''}`}
              style={{
                '--status-color': STATUS_TYPE_CONFIG.custom.textColor,
                '--status-bg': STATUS_TYPE_CONFIG.custom.bgColor,
              } as React.CSSProperties}
              onClick={() => setSelectedType('custom')}
            >
              <span className={styles.statusTypeDot} style={{ background: STATUS_TYPE_CONFIG.custom.textColor }} />
              <span className={styles.statusTypeLabel}>{t('modelStatus.custom')}</span>
            </button>
          </div>

          {/* Custom text */}
          <div className={styles.fieldLabel}>{t('modelStatus.statusText')}</div>
          <input
            className={styles.textInput}
            type="text"
            value={customText}
            onChange={e => setCustomText(e.target.value)}
            placeholder={t('modelStatus.statusTextPlaceholder')}
            maxLength={200}
          />

          {/* Expiration */}
          <div className={styles.fieldLabel}>{t('modelStatus.autoDelete')}</div>
          <div className={styles.expirationRow}>
            {EXPIRATION_OPTIONS.map(opt => (
              <button
                key={opt.hours}
                className={`${styles.expirationChip} ${expiresInHours === opt.hours ? styles.selected : ''}`}
                onClick={() => setExpiresInHours(opt.hours)}
              >
                {t(opt.labelKey)}
              </button>
            ))}
          </div>

          {/* Visibility toggles */}
          <div className={styles.settingsRow}>
            <span className={styles.settingLabel}>{t('modelStatus.inSelector')}</span>
            <button
              className={`${styles.toggle} ${showInSelector ? styles.on : ''}`}
              onClick={() => setShowInSelector(!showInSelector)}
            >
              <span className={styles.toggleThumb} />
            </button>
          </div>
          <div className={styles.settingsRow}>
            <span className={styles.settingLabel}>{t('modelStatus.inComposer')}</span>
            <button
              className={`${styles.toggle} ${showInComposer ? styles.on : ''}`}
              onClick={() => setShowInComposer(!showInComposer)}
            >
              <span className={styles.toggleThumb} />
            </button>
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            {existingStatus && (
              <button className={styles.deleteButton} onClick={handleDelete} disabled={isSaving}>
                {t('btn.delete')}
              </button>
            )}
            <button
              className={styles.saveButton}
              onClick={handleSave}
              disabled={!selectedType || isSaving}
            >
              {isSaving ? t('btn.saving') : (existingStatus ? t('btn.update') : t('btn.save'))}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModelStatusEditor;
